import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import callApi from "helpers/callApi";
import { getApiErrorMessage } from "utils/errors";

const initialState = {
  products: [],
  addingProduct: false,
  addingProductError: null,
  fetchingProducts: false,
  fetchingProductsError: null,
  updatingStatus: false,
  updatingStatusError: null,
  updatingProduct: false,
  updatingProductError: null,
  deletingProduct: false,
  deletingProductError: null,
};

export const fetchAuthorizeProducts = createAsyncThunk(
  "authorize-products/fetchProducts",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append("app_id", "6");
      
      if (filters.search) {
        params.append("search", filters.search);
      }
      if (filters.status) {
        params.append("status", filters.status);
      }
      if (filters.active_from) {
        params.append("active_from", filters.active_from);
      }
      if (filters.active_to) {
        params.append("active_to", filters.active_to);
      }
      if (filters.type) {
        params.append("type", filters.type);
      }

      const queryString = params.toString();
      const url = `/products/?${queryString}`;

      const response = await callApi(url, "GET");
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const updateProductStatus = createAsyncThunk(
  "authorize-products/updateStatus",
  async ({ productId, status }, { rejectWithValue }) => {
    try {
      const response = await callApi(`/products/${productId}/status`, "PUT", {
        status: status,
      });

      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Error"));
    }
  }
);

export const deleteAuthorizeProduct = createAsyncThunk(
  "authorize-products/deleteProduct",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await callApi(`/products/${productId}`, "DELETE");

      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }
      
      try {
        const data = await response.json();
        return data;
      } catch {
        return { success: true };
      }
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Error"));
    }
  }
);

const withOptionalWebsiteId = (body, productData) => {
  if (Number.isInteger(productData?.website_id)) {
    return { ...body, website_id: productData.website_id };
  }
  return body;
};

export const updateAuthorizeProduct = createAsyncThunk(
  "authorize-products/updateProduct",
  async ({ productId, productData }, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/products/${productId}`,
        "PUT",
        withOptionalWebsiteId(
          {
            sku: productData.sku || "",
            description: productData.description || "",
            active_from: productData.active_from || null,
            active_to: productData.active_to || null,
            app: [6],
            comment: productData.comment || "",
            type:
              productData.type === "package" || productData.type === "product"
                ? productData.type
                : "product",
            target_market: productData.target_market ?? null,
            representative_type: productData.representative_type ?? null,
            representative_company_name:
              productData.representative_company_name ?? "",
            representative_address_line:
              productData.representative_address_line ?? "",
            representative_postal_code:
              productData.representative_postal_code ?? "",
            representative_city: productData.representative_city ?? "",
            representative_country_id:
              productData.representative_country_id ?? null,
            representative_email: productData.representative_email ?? "",
          },
          productData
        )
      );

      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Error"));
    }
  }
);

export const addAuthorizeProduct = createAsyncThunk(
  "authorize-products/addProduct",
  async (productData, { rejectWithValue }) => {
    try {
      const response = await callApi(
        "/products/",
        "POST",
        withOptionalWebsiteId(
          {
            sku: productData.sku || "",
            description: productData.description || "",
            active_from: productData.active_from || null,
            active_to: productData.active_to || null,
            app: [6],
            comment: productData.comment || "",
            type:
              productData.type === "package" || productData.type === "product"
                ? productData.type
                : "product",
            target_market: productData.target_market ?? null,
            representative_type: productData.representative_type ?? null,
            representative_company_name:
              productData.representative_company_name ?? "",
            representative_address_line:
              productData.representative_address_line ?? "",
            representative_postal_code:
              productData.representative_postal_code ?? "",
            representative_city: productData.representative_city ?? "",
            representative_country_id:
              productData.representative_country_id ?? null,
            representative_email: productData.representative_email ?? "",
          },
          productData
        )
      );

      const responseData = await response.json().catch(() => ({}));

      // Preserve subscription cart body on any HTTP status (same as sendingEinvoices)
      if (responseData?.to_buy) {
        return {
          ...responseData,
          __subscriptionRequired: true,
          __httpStatus: response.status,
        };
      }

      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }

      return responseData;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Error"));
    }
  }
);

const authorizeProductsSlice = createSlice({
  name: "authorizeProducts",
  initialState,
  reducers: {
    clearAddingError: (state) => {
      state.addingProductError = null;
    },
    clearFetchingError: (state) => {
      state.fetchingProductsError = null;
    },
    clearUpdatingStatusError: (state) => {
      state.updatingStatusError = null;
    },
    clearDeletingError: (state) => {
      state.deletingProductError = null;
    },
    clearUpdatingProductError: (state) => {
      state.updatingProductError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuthorizeProducts.pending, (state) => {
        state.fetchingProducts = true;
        state.fetchingProductsError = null;
      })
      .addCase(fetchAuthorizeProducts.fulfilled, (state, action) => {
        state.fetchingProducts = false;
        state.fetchingProductsError = null;
        state.products = action.payload || [];
      })
      .addCase(fetchAuthorizeProducts.rejected, (state, action) => {
        state.fetchingProducts = false;
        state.fetchingProductsError = action.payload || "Failed to fetch products";
      })
      .addCase(addAuthorizeProduct.pending, (state) => {
        state.addingProduct = true;
        state.addingProductError = null;
      })
      .addCase(addAuthorizeProduct.fulfilled, (state) => {
        state.addingProduct = false;
        state.addingProductError = null;
      })
      .addCase(addAuthorizeProduct.rejected, (state, action) => {
        state.addingProduct = false;
        state.addingProductError = action.payload || "Failed to add product";
      })
      .addCase(updateAuthorizeProduct.pending, (state) => {
        state.updatingProduct = true;
        state.updatingProductError = null;
      })
      .addCase(updateAuthorizeProduct.fulfilled, (state) => {
        state.updatingProduct = false;
        state.updatingProductError = null;
      })
      .addCase(updateAuthorizeProduct.rejected, (state, action) => {
        state.updatingProduct = false;
        state.updatingProductError = action.payload || "Failed to update product";
      })
      .addCase(updateProductStatus.pending, (state) => {
        state.updatingStatus = true;
        state.updatingStatusError = null;
      })
      .addCase(updateProductStatus.fulfilled, (state) => {
        state.updatingStatus = false;
        state.updatingStatusError = null;
      })
      .addCase(updateProductStatus.rejected, (state, action) => {
        state.updatingStatus = false;
        state.updatingStatusError = action.payload || "Failed to update product status";
      })
      .addCase(deleteAuthorizeProduct.pending, (state) => {
        state.deletingProduct = true;
        state.deletingProductError = null;
      })
      .addCase(deleteAuthorizeProduct.fulfilled, (state) => {
        state.deletingProduct = false;
        state.deletingProductError = null;
      })
      .addCase(deleteAuthorizeProduct.rejected, (state, action) => {
        state.deletingProduct = false;
        state.deletingProductError = action.payload || "Failed to delete product";
      });
  },
});

export const { 
  clearAddingError, 
  clearFetchingError, 
  clearUpdatingStatusError, 
  clearUpdatingProductError, 
  clearDeletingError 
} = authorizeProductsSlice.actions;
export default authorizeProductsSlice.reducer;
